import { action, computed, observable } from 'mobx';
import { keyBy } from 'lodash';
import { DateTime } from 'luxon';
import { LIVE_CLASS_CANCELLATION_WINDOW } from 'config/constants';
import type { LiveVirtualClass } from 'types/liveClass';
import {
  getPartnerLvcEnrollments,
  getPartnerUserLvcOfferings,
} from 'services/PartnerEnrollmentService';
import type {
  ClassAllocationSummary,
  ClassScheduleTimes,
  LiveClassQuotaStatus,
  PartnerEnrollmentsRouter,
  PartnerLvcEnrollment,
  PartnerLvcOffering,
  PartnerOrderRow,
  PartnerScheduleBlockReason,
  PartnerStudentEnrollmentRow,
  PartnerStudentFilterKey,
} from 'types/partnerEnrollment';
import {
  enrollmentMatchesFilter,
  getFilterConfigurations,
  isPartnerStudentFilterKey,
  PARTNER_SUBSCRIPTIONS_ROUTE_NAME,
  PARTNER_STUDENT_FILTER_GROUP_KEY,
  PARTNER_STUDENT_FILTER_URL_KEY,
} from './config';

function deriveStatus(
  enrollment: PartnerLvcEnrollment,
  liveClass?: PartnerLvcOffering | null,
): LiveClassQuotaStatus {
  if (enrollment.start_time) {
    const start = DateTime.fromISO(enrollment.start_time);
    if (start.isValid && start > DateTime.utc()) {
      return 'unavailable';
    }
  }
  if (liveClass) {
    return 'scheduled';
  }
  return 'unscheduled';
}

function daysUntilStart(startTime?: string): number | null {
  if (!startTime) {
    return null;
  }
  const start = DateTime.fromISO(startTime);
  if (!start.isValid || start <= DateTime.utc()) {
    return null;
  }
  return Math.max(0, Math.ceil(start.diff(DateTime.utc(), 'days').days));
}

function buildAllocation(
  enrollments: PartnerStudentEnrollmentRow[],
): ClassAllocationSummary {
  const scheduledCount = enrollments.filter(
    (e) => e.status === 'scheduled',
  ).length;
  const unscheduledCount = enrollments.filter(
    (e) => e.status === 'unscheduled',
  ).length;
  const unavailableCount = enrollments.filter(
    (e) => e.status === 'unavailable',
  ).length;

  return {
    scheduledCount,
    unscheduledCount,
    unavailableCount,
    total: enrollments.length,
  };
}

function enrollmentRows(
  enrollments: PartnerLvcEnrollment[],
  offerings: PartnerLvcOffering[],
): PartnerStudentEnrollmentRow[] {
  const offeringByEntitlement = keyBy(offerings, 'entitlement_id');
  const partnerEntitlementIds = new Set(
    enrollments.map((enrollment) => enrollment.enrollment_id),
  );

  const relatedByUserAndCode = keyBy(
    offerings.filter(
      (offering) =>
        Boolean(offering.entitlement_id) &&
        !partnerEntitlementIds.has(offering.entitlement_id) &&
        Boolean(offering.username) &&
        Boolean(offering.offering_code),
    ),
    (offering) =>
      `${offering.username.toLowerCase()}::${offering.offering_code!.toLowerCase()}`,
  );

  return enrollments.map((enrollment) => {
    let liveClass = offeringByEntitlement[enrollment.enrollment_id] ?? null;
    if (!liveClass) {
      const username = enrollment.username?.toLowerCase();
      const offeringCode = enrollment.offering_code?.toLowerCase();
      if (username && offeringCode) {
        liveClass =
          relatedByUserAndCode[`${username}::${offeringCode}`] ?? null;
      }
    }
    return {
      ...enrollment,
      status: deriveStatus(enrollment, liveClass),
      liveClass,
      daysUntilStart: daysUntilStart(enrollment.start_time),
    };
  });
}

export default class PartnerStudentsStore {
  @observable isLoading = false;

  @observable loadError: string | null = null;

  @observable enrollments: PartnerLvcEnrollment[] = [];

  @observable offerings: PartnerLvcOffering[] = [];

  @observable selectedFilters = new Set<PartnerStudentFilterKey>();

  @observable expandedFilterGroups = new Set([
    PARTNER_STUDENT_FILTER_GROUP_KEY,
  ]);

  routerStore?: PartnerEnrollmentsRouter;

  constructor() {
    this.load();
  }

  setRouterStore(routerStore: PartnerEnrollmentsRouter) {
    this.routerStore = routerStore;
  }

  @action applyFiltersFromRoute(routeParams: Record<string, string> = {}) {
    const statusParam = routeParams[PARTNER_STUDENT_FILTER_URL_KEY];
    if (!statusParam?.trim()) {
      this.selectedFilters = new Set();
      return;
    }

    const keys = statusParam
      .split(',')
      .map((item) => item.trim())
      .filter(isPartnerStudentFilterKey);

    this.selectedFilters = new Set(keys);
  }

  @action updateUrlFilters() {
    if (
      !this.routerStore?.route ||
      this.routerStore.route.name !== PARTNER_SUBSCRIPTIONS_ROUTE_NAME
    ) {
      return;
    }

    const routeParams = this.routerStore.route.params ?? {};
    const status =
      this.selectedFilters.size > 0
        ? Array.from(this.selectedFilters).join(',')
        : undefined;

    this.routerStore.navigate(
      PARTNER_SUBSCRIPTIONS_ROUTE_NAME,
      {
        ...routeParams,
        [PARTNER_STUDENT_FILTER_URL_KEY]: status,
      },
      { replace: true },
    );
  }

  @action toggleFilter(key: PartnerStudentFilterKey) {
    const next = new Set(this.selectedFilters);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.selectedFilters = next;
    this.updateUrlFilters();
  }

  @action toggleFilterGroups = (id: string, expanded?: boolean) => {
    const next = new Set(this.expandedFilterGroups);
    if (expanded) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.expandedFilterGroups = next;
  };

  @action async load() {
    this.isLoading = true;
    this.loadError = null;
    try {
      const [enrollments, offerings] = await Promise.all([
        getPartnerLvcEnrollments(),
        getPartnerUserLvcOfferings(),
      ]);
      this.enrollments = enrollments;
      this.offerings = offerings;
    } catch (error) {
      this.enrollments = [];
      this.offerings = [];
      this.loadError =
        error instanceof Error ? error.message : 'Failed to load orders';
    } finally {
      this.isLoading = false;
    }
  }

  @computed get orders(): PartnerOrderRow[] {
    const byOrder = new Map<string, PartnerStudentEnrollmentRow[]>();

    enrollmentRows(this.enrollments, this.offerings).forEach((row) => {
      const orderKey = `${row.lms_order_number}::${row.sku ?? ''}`;
      const list = byOrder.get(orderKey) || [];
      list.push(row);
      byOrder.set(orderKey, list);
    });

    return Array.from(byOrder.entries()).map(([orderGroupKey, enrollments]) => {
      const orderNumber = enrollments[0].lms_order_number!;
      const sku = enrollments[0].sku || '—';
      const orderDates = enrollments
        .map((e) => e.lms_ordered_date)
        .filter((t): t is string => !!t)
        .sort();

      return {
        orderGroupKey,
        orderNumber,
        orderDate: orderDates[0] || null,
        sku,
        learnerCount: enrollments.length,
        allocation: buildAllocation(enrollments),
        enrollments,
      };
    });
  }

  @computed get filterCounts(): Record<PartnerStudentFilterKey, number> {
    const rows = enrollmentRows(this.enrollments, this.offerings);
    const configs = getFilterConfigurations();

    return configs.reduce(
      (counts, config) => ({
        ...counts,
        [config.key]: rows.filter((row) => config.validateEntry(row)).length,
      }),
      {} as Record<PartnerStudentFilterKey, number>,
    );
  }

  @computed get filteredOrders(): PartnerOrderRow[] {
    if (this.selectedFilters.size === 0) {
      return this.orders;
    }
    const selected = Array.from(this.selectedFilters);
    return this.orders.filter((order) =>
      order.enrollments.some((enrollment) =>
        selected.some((key) => enrollmentMatchesFilter(enrollment, key)),
      ),
    );
  }

  @computed get orderCount() {
    return this.filteredOrders.length;
  }

  /** Within cancellation window or class already started. */
  static isClassCancellationLocked(
    live: ClassScheduleTimes | null | undefined,
  ): boolean {
    if (!live?.start_time) {
      return false;
    }
    const days = daysUntilStart(live.start_time);

    if (days === null) {
      const start = DateTime.fromISO(live.start_time);
      return start.isValid && start <= DateTime.utc();
    }
    return days <= LIVE_CLASS_CANCELLATION_WINDOW;
  }

  static isDifferentSubscriptionBooking(
    live: PartnerLvcOffering | null | undefined,
    enrollment?: Pick<PartnerStudentEnrollmentRow, 'enrollment_id'> | null,
  ): boolean {
    return Boolean(
      live?.entitlement_id &&
        enrollment?.enrollment_id &&
        live.entitlement_id !== enrollment.enrollment_id,
    );
  }

  static isScheduledClassViewDisabled(
    live: PartnerLvcOffering | null | undefined,
    enrollment?: Pick<PartnerStudentEnrollmentRow, 'enrollment_id'> | null,
  ): boolean {
    if (PartnerStudentsStore.isDifferentSubscriptionBooking(live, enrollment)) {
      return true;
    }
    if (live?.end_time) {
      const end = DateTime.fromISO(live.end_time);
      if (end.isValid && end <= DateTime.utc()) {
        return false;
      }
    }
    return PartnerStudentsStore.isClassCancellationLocked(live);
  }

  static isLiveClassInProgress(
    live: LiveVirtualClass | null | undefined,
  ): boolean {
    if (!live?.start_time) {
      return false;
    }
    const tz = live.timezone ?? 'UTC';
    const now = DateTime.now().setZone(tz);
    const start = DateTime.fromISO(live.start_time, { zone: tz });
    if (!start.isValid || now < start) {
      return false;
    }
    if (live.end_time) {
      const end = DateTime.fromISO(live.end_time, { zone: tz });
      if (end.isValid && now >= end) {
        return false;
      }
    }
    return true;
  }

  static pickPartnerLearnerSeat(
    enrollments: PartnerLvcEnrollment[],
    userUuid: string,
    offeringCode: string,
  ): PartnerLvcEnrollment | null {
    const uuid = userUuid;
    const code = offeringCode.toLowerCase();
    const matchingSeats = enrollments.filter(
      (enrollment) =>
        enrollment.user_uuid === uuid &&
        enrollment.offering_code?.toLowerCase() === code,
    );
    if (!matchingSeats.length) {
      return null;
    }

    const now = DateTime.utc();
    const activeSeat = matchingSeats.find((enrollment) => {
      const start = enrollment.start_time
        ? DateTime.fromISO(enrollment.start_time)
        : null;
      const end = enrollment.end_time
        ? DateTime.fromISO(enrollment.end_time)
        : null;
      const started = !start?.isValid || start <= now;
      const notEnded = !end?.isValid || end >= now;
      return started && notEnded;
    });
    if (activeSeat) {
      return activeSeat;
    }

    const futureSeats = matchingSeats
      .filter((enrollment) => {
        const start = enrollment.start_time
          ? DateTime.fromISO(enrollment.start_time)
          : null;
        return start?.isValid && start > now;
      })
      .sort((a, b) => {
        const aStart = DateTime.fromISO(a.start_time!);
        const bStart = DateTime.fromISO(b.start_time!);
        return aStart.toMillis() - bStart.toMillis();
      });
    if (futureSeats.length) {
      return futureSeats[0];
    }
    return matchingSeats[0];
  }

  static resolveScheduleBlockReason(
    seat: PartnerLvcEnrollment | null | undefined,
    enrolledForCourse: LiveVirtualClass | null | undefined,
  ): PartnerScheduleBlockReason | null {
    const now = DateTime.utc();

    if (seat?.end_time) {
      const end = DateTime.fromISO(seat.end_time);
      if (end.isValid && end < now) {
        return 'expired';
      }
    }

    if (seat?.start_time) {
      const start = DateTime.fromISO(seat.start_time);
      if (start.isValid && start > now) {
        return 'future';
      }
    }

    if (
      seat?.enrollment_id &&
      enrolledForCourse?.entitlement_id &&
      seat.enrollment_id !== enrolledForCourse.entitlement_id
    ) {
      return 'different_subscription';
    }

    if (
      enrolledForCourse &&
      PartnerStudentsStore.isClassCancellationLocked(enrolledForCourse)
    ) {
      return 'in_progress';
    }

    return null;
  }

  static formatScheduleAlertDate(iso?: string): string {
    if (!iso) {
      return '';
    }
    const parsed = DateTime.fromISO(iso);
    return parsed.isValid ? parsed.toFormat('yyyy/MM/dd') : '';
  }
}
